import React, { useState, useCallback } from 'react';
import { authFetch } from '../../utils/authHelpers';
import {
  Box, Paper, Typography, Button, FormControl, InputLabel, Select, MenuItem,
  LinearProgress, Alert, Chip, IconButton, Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import TranslateIcon from '@mui/icons-material/Translate';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';

import { AI_ENGINE_URL } from '../../utils/apiConfig';

function BatchTranslate() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]); // [{name, content}, ...]
  const [sourceLanguage, setSourceLanguage] = useState('nl');
  const [targetLanguage, setTargetLanguage] = useState('en');
  const [translating, setTranslating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null); // BatchTranslationResponse
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFiles(prev => [...prev, { name: file.name, content: e.target.result }]);
      };
      reader.readAsText(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/plain': ['.srt'], 'application/x-subrip': ['.srt'] },
    multiple: true
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDownload = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleTranslate = async () => {
    if (files.length === 0) return;

    setTranslating(true);
    setProgress(0);
    setError(null);
    setResults(null);

    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/srt-jobs/translate-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: files.map(f => ({ filename: f.name, srt_content: f.content })),
          source_language: sourceLanguage,
          target_language: targetLanguage
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Batch vertaling mislukt');
      }

      const data = await response.json();
      setResults(data);
      setProgress(100);
    } catch (err) {
      setError(err.message);
    } finally {
      setTranslating(false);
    }
  };

  const handleDownloadAll = () => {
    if (!results) return;
    const suffix = targetLanguage === 'en' ? 'EN' : 'NL';
    results.results.forEach(r => {
      if (r.success && r.translated_srt) {
        const newName = r.filename.replace(/\.srt$/i, `_${suffix}.srt`);
        handleDownload(r.translated_srt, newName);
      }
    });
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Batch Vertaling
        </Typography>
        <Button variant="outlined" size="small" onClick={() => navigate('/SRTGenerator')}>
          Terug naar SRT Generator
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Vertaal meerdere SRT bestanden tegelijk. Timing blijft identiek per bestand.
      </Typography>

      {/* File Upload */}
      <Paper
        variant="outlined"
        sx={{
          p: 3, mb: 3, textAlign: 'center', cursor: 'pointer',
          borderStyle: 'dashed', borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: isDragActive ? 'action.hover' : 'transparent'
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography>
          {isDragActive ? 'Drop SRT bestanden hier...' : 'Sleep SRT bestanden hierheen of klik om te selecteren'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Meerdere .srt bestanden toegestaan
        </Typography>
      </Paper>

      {/* File List */}
      {files.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {files.length} bestand{files.length !== 1 ? 'en' : ''} geselecteerd
          </Typography>
          {files.map((file, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
              <Chip label={file.name} size="small" variant="outlined" />
              <IconButton size="small" onClick={() => removeFile(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Paper>
      )}

      {/* Language Selection */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Brontaal</InputLabel>
          <Select value={sourceLanguage} label="Brontaal" onChange={(e) => {
            setSourceLanguage(e.target.value);
            setTargetLanguage(e.target.value === 'nl' ? 'en' : 'nl');
          }}>
            <MenuItem value="nl">Nederlands</MenuItem>
            <MenuItem value="en">Engels</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TranslateIcon color="action" />
        </Box>
        <FormControl fullWidth>
          <InputLabel>Doeltaal</InputLabel>
          <Select value={targetLanguage} label="Doeltaal" onChange={(e) => setTargetLanguage(e.target.value)}>
            <MenuItem value="nl">Nederlands</MenuItem>
            <MenuItem value="en">Engels</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Translate Button */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleTranslate}
        disabled={files.length === 0 || translating}
        startIcon={translating ? null : <TranslateIcon />}
        sx={{ mb: 3 }}
      >
        {translating ? 'Vertalen...' : `Vertaal ${files.length} bestand${files.length !== 1 ? 'en' : ''}`}
      </Button>

      {translating && <LinearProgress sx={{ mb: 3 }} />}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Results */}
      {results && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2">
              Resultaten: {results.succeeded}/{results.total} geslaagd
            </Typography>
            {results.succeeded > 1 && (
              <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadAll}>
                Download alle
              </Button>
            )}
          </Box>
          {results.results.map((result, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderTop: index > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {result.success ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <Tooltip title={result.error || 'Mislukt'}>
                    <ErrorIcon color="error" fontSize="small" />
                  </Tooltip>
                )}
                <Typography variant="body2">{result.filename}</Typography>
              </Box>
              {result.success && result.translated_srt && (
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    const suffix = targetLanguage === 'en' ? 'EN' : 'NL';
                    handleDownload(result.translated_srt, result.filename.replace(/\.srt$/i, `_${suffix}.srt`));
                  }}
                >
                  Download
                </Button>
              )}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}

export default BatchTranslate;
